import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { ConstantUtil } from '@/utils/ConstantUtil'
import { deviceService } from '@/utils/deviceService'
import { LogService } from '@/utils/logService'
import { encryptionService } from '@/services/encryption/encryptionService'
import {
  ICreateOrderParams,
  ICreateOrderResponse,
  IPaymentParams,
  IPaymentResponse,
  ICancelOrderResponse,
} from '@/types/order'
import {
  IVerifyCouponParams,
  IVerifyCouponResponse,
  IGetAvailablePromotionsParams,
  IGetAvailablePromotionsResponse,
} from '@/types/promotion'
import { IRefreshTokenResponse } from '@/types/auth'

// ===============================
// 🔧 基礎設定
// ===============================
const baseQuery = fetchBaseQuery({
  baseUrl: ConstantUtil.API_BASE_URL,
  prepareHeaders: (headers) => {
    // 1️⃣ 加入 satoken
    const token = localStorage.getItem(ConstantUtil.STORAGE_USER_TOKEN)
    if (token) {
      headers.set('satoken', token)
      try {
        const tokenPayload = JSON.parse(atob(token.split('.')[1]))
        LogService.info('Order API Token 使用追蹤', {
          userId: tokenPayload.sub || tokenPayload.id,
          userType: tokenPayload.type || 'unknown',
          issuedAt: new Date(tokenPayload.iat * 1000).toISOString(),
          expiresAt: new Date(tokenPayload.exp * 1000).toISOString(),
        })
      } catch (error) {
        LogService.warn('無法解析 JWT token', { error: (error as Error).message })
      }
    }

    // 2️⃣ 加入裝置資訊
    const deviceData = deviceService.getDeviceInfo()
    headers.set('deviceData', encodeURIComponent(JSON.stringify(deviceData)))

    // 3️⃣ 設定內容類型
    headers.set('Content-Type', 'application/json')

    return headers
  },
  timeout: ConstantUtil.API_TIMEOUT,
})

// ===============================
// 🔒 加密 + Token Refresh 包裝
// ===============================
const baseQueryWithEncryption = async (args: any, api: any, extraOptions: any) => {
  let modifiedArgs = args

  try {
    // 1️⃣ 傳送前：加密 body
    if (args?.body && typeof args.body === 'object') {
      const encrypted = encryptionService.encryptData(args.body)
      modifiedArgs = {
        ...args,
        body: JSON.stringify({ data: encrypted }),
      }
    }

    // 2️⃣ 執行請求
    let result = await baseQuery(modifiedArgs, api, extraOptions)

    // 3️⃣ Token 過期處理
    if (result.error && result.error.status === 401) {
      console.warn('⚠️ Access Token 過期，開始嘗試 Refresh Token')

      const refreshToken = localStorage.getItem(ConstantUtil.STORAGE_USER_REFRESH_TOKEN)
      if (!refreshToken) return result

      const rawBaseQuery = fetchBaseQuery({
        baseUrl: ConstantUtil.API_BASE_URL,
        prepareHeaders: (headers) => {
          headers.set('Content-Type', 'application/json')
          return headers
        },
      })

      const refreshResult = await rawBaseQuery(
        {
          url: ConstantUtil.API_REFRESH_TOKEN,
          method: 'POST',
          body: { refreshToken },
        },
        api,
        extraOptions
      )

      const refreshBody = refreshResult.data as IRefreshTokenResponse | undefined
      const refreshData = refreshBody?.data

      if (refreshData?.accessToken) {
        const { accessToken, refreshToken: newRefreshToken } = refreshData
        localStorage.setItem(ConstantUtil.STORAGE_USER_TOKEN, accessToken)
        if (newRefreshToken) {
          localStorage.setItem(ConstantUtil.STORAGE_USER_REFRESH_TOKEN, newRefreshToken)
        }

        // 🔁 用新 Token 重試原請求
        return await baseQuery(modifiedArgs, api, extraOptions)
      }

      // ❌ 無法刷新，導回登入頁
      alert('您的登入已逾時，請重新登入')
      localStorage.removeItem(ConstantUtil.STORAGE_USER_TOKEN)
      localStorage.removeItem(ConstantUtil.STORAGE_USER_REFRESH_TOKEN)
      localStorage.removeItem(ConstantUtil.STORAGE_USER_INFO)
      window.location.href = '/login'
    }

    // 4️⃣ 嘗試解密回應
    if (result?.data && typeof result.data === 'object') {
      const responseData = result.data as any
      if (responseData.data && typeof responseData.data === 'string') {
        try {
          const decrypted = encryptionService.decryptData(responseData.data)
          result.data = {
            ...responseData,
            data: decrypted,
          }
        } catch (e) {
          LogService.warn('回應解密失敗，可能是明文回應', { error: e })
        }
      }
    }

    // 5️⃣ 補上 success/message 格式
    if (result.data && typeof result.data === 'object' && !('success' in result.data)) {
      result.data = {
        success: true,
        data: result.data,
        message: 'Success',
      }
    }

    if (result.data) {
      LogService.info('Order API 回應', result.data as Record<string, unknown>)
    }

    return result
  } catch (error) {
    LogService.error('Order API baseQueryWithEncryption 錯誤', error)
    throw error
  }
}

// ===============================
// 📦 訂單 API
// ===============================
export const orderApi = createApi({
  reducerPath: 'orderApi',
  baseQuery: baseQueryWithEncryption,
  tagTypes: ['Order'],
  endpoints: (builder) => ({
    getOrders: builder.query<ICreateOrderResponse[], void>({
      query: () => ConstantUtil.API_ORDERS,
      providesTags: ['Order'],
    }),

    getOrderById: builder.query<ICreateOrderResponse, string>({
      query: (id: string) => `${ConstantUtil.API_ORDERS}/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Order', id }],
    }),

    createOrder: builder.mutation<ICreateOrderResponse, ICreateOrderParams>({
      query: (orderData) => ({
        url: ConstantUtil.API_ORDERS,
        method: 'POST',
        body: orderData,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          LogService.logApiCall(
            ConstantUtil.API_ORDERS,
            'POST',
            undefined,
            data as unknown as Record<string, unknown>
          )
        } catch (error) {
          LogService.error('Create Order API call failed', error)
        }
      },
      invalidatesTags: ['Order'],
    }),

    processPayment: builder.mutation<IPaymentResponse, IPaymentParams>({
      query: (paymentData) => ({
        url: '${ConstantUtil.API_ORDERS}/payment',
        method: 'POST',
        body: {
          paymentMethod: paymentData.paymentMethod,
          paymentDetails: paymentData.paymentDetails,
        },
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          LogService.logApiCall(
            'ConstantUtil.API_ORDERS_PAYMENT',
            'POST',
            undefined,
            data as unknown as Record<string, unknown>
          )
        } catch (error) {
          LogService.error('Process Payment API call failed', error)
        }
      },
    }),

    cancelOrder: builder.mutation<ICancelOrderResponse, string>({
      query: (orderId: string) => ({
        url: ConstantUtil.API_ORDERS,
        method: 'POST',
        body: {
          orderId: orderId
        },
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          LogService.logApiCall(
            ConstantUtil.API_ORDERS,
            'POST',
            undefined,
            data as unknown as Record<string, unknown>
          )
        } catch (error) {
          LogService.error('Cancel Order API call failed', error)
        }
      },
      invalidatesTags: ['Order'],
    }),

    verifyCoupon: builder.mutation<IVerifyCouponResponse, IVerifyCouponParams>({
      query: (params) => ({
        url: `${ConstantUtil.API_ORDERS}/coupons/verify`,
        method: 'POST',
        body: params,
      }),
    }),

    getAvailablePromotions: builder.query<IGetAvailablePromotionsResponse, IGetAvailablePromotionsParams>({
      query: (params) => ({
        url: `${ConstantUtil.API_ORDERS}/promotions/available`,
        method: 'POST',
        body: params,
      }),
    }),
  }),
})

export const {
  useGetOrdersQuery,
  useGetOrderByIdQuery,
  useCreateOrderMutation,
  useCancelOrderMutation,
  useVerifyCouponMutation,
  useGetAvailablePromotionsQuery,
  useProcessPaymentMutation,
} = orderApi
